/**
 * PokeCCC per-country free shipping thresholds.
 *
 * The server renders the threshold for `localization.country`, which Shopify
 * only knows once the visitor has picked a country. This refines the marquee
 * label and the cart drawer progress bar using the geo country detected by
 * pokeccc-locale.js — unless the visitor explicitly chose a country in the
 * native localization form, in which case their choice always wins.
 *
 * Cart drawer re-renders replace the bar's DOM, so adjustments are re-applied
 * via a MutationObserver scoped to the drawer.
 */
(function () {
  'use strict';

  var LOG_PREFIX = '[PokecccFreeShipping]';

  var core = window.PokeCCC;
  if (!core || !core.ready) {
    console.warn(LOG_PREFIX, 'Shared locale runtime unavailable');
    return;
  }

  var config = core.config;

  /* ------------------------------------------------------------------ */
  /* Manual country choice                                               */
  /* ------------------------------------------------------------------ */

  function isCountryForm(el) {
    return !!(el && el.querySelector('input[name="country_code"]'));
  }

  function watchCountrySelection() {
    // localization-form submits via form.submit(), which fires no submit
    // event — catch the option click instead, plus submit as a fallback.
    document.addEventListener(
      'click',
      function (event) {
        if (!event.target || !event.target.closest) return;
        var option = event.target.closest('localization-form a[data-value]');
        if (option && isCountryForm(option.closest('localization-form'))) {
          core.prefs.setCountry();
        }
      },
      true
    );
    document.addEventListener(
      'submit',
      function (event) {
        if (isCountryForm(event.target)) core.prefs.setCountry();
      },
      true
    );
  }

  /* ------------------------------------------------------------------ */
  /* Marquee                                                             */
  /* ------------------------------------------------------------------ */

  function applyMarqueeLabel(label) {
    var messages = document.querySelectorAll('[data-fs-message]');
    if (!messages.length) return;

    if (label) {
      document.querySelectorAll('[data-fs-amount]').forEach(function (el) {
        el.textContent = label;
      });
      messages.forEach(function (el) {
        el.removeAttribute('hidden');
      });
    } else {
      messages.forEach(function (el) {
        el.setAttribute('hidden', '');
      });
    }
  }

  /* ------------------------------------------------------------------ */
  /* Money formatting                                                    */
  /* ------------------------------------------------------------------ */

  function formatWithDelimiters(cents, precision, thousands, decimal) {
    var parts = (cents / 100).toFixed(precision).split('.');
    var units = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands);
    return parts[1] ? units + decimal + parts[1] : units;
  }

  /**
   * Renders cents in the shop money format (e.g.
   * "<span class=cvc-money>{{amount_with_comma_separator}} Kč</span>")
   * so the Convercy currency app converts the injected amount for display
   * exactly like server-rendered prices.
   */
  function formatMoney(cents, format, currencyCode) {
    var match = format && format.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!match) return (cents / 100).toFixed(2) + ' ' + currencyCode;

    var value;
    switch (match[1]) {
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_space_separator':
        value = formatWithDelimiters(cents, 2, ' ', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = formatWithDelimiters(cents, 0, ' ', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 2, "'", '.');
        break;
      default:
        value = formatWithDelimiters(cents, 2, ',', '.');
    }
    return format.replace(match[0], value);
  }

  /* ------------------------------------------------------------------ */
  /* Cart drawer bar                                                     */
  /* ------------------------------------------------------------------ */

  function renderBar(bar, total, entry) {
    var unlocked = total >= entry.cents;
    var percent = Math.min((total / entry.cents) * 100, 100);
    var fill = bar.querySelector('.free-shipping-bar__fill');
    var label = bar.querySelector('.free-shipping-bar__label');

    bar.classList.toggle('free-shipping-bar--unlocked', unlocked);
    if (fill) fill.style.width = percent + '%';

    if (label) {
      if (unlocked) {
        label.innerHTML = bar.getAttribute('data-fs-label-unlocked') || '';
      } else {
        var template = bar.getAttribute('data-fs-label-below') || '';
        label.innerHTML = template.replace(
          '[[amount]]',
          formatMoney(
            entry.cents - total,
            bar.getAttribute('data-fs-money-format'),
            entry.currency
          )
        );
      }
    }

    bar.removeAttribute('hidden');
  }

  function adjustCartBars(countryCode) {
    var bars = document.querySelectorAll(
      '[data-fs-bar]:not([data-fs-adjusted])'
    );
    if (!bars.length) return;

    var entry = countryCode ? config.thresholds[countryCode] : null;

    bars.forEach(function (bar) {
      bar.setAttribute('data-fs-adjusted', '');

      // Unknown country: the server-rendered state is all we have.
      if (!countryCode) return;

      // Known country with no free shipping tier. The marquee hides itself in
      // this case, so the bar must too — otherwise the cart promises free
      // shipping that no shipping zone actually offers.
      if (!entry) {
        bar.setAttribute('hidden', '');
        return;
      }

      if (entry.currency !== bar.getAttribute('data-fs-currency')) return;

      var total = parseInt(bar.getAttribute('data-fs-cart-total'), 10);
      if (isNaN(total)) return;

      var serverThreshold = parseInt(bar.getAttribute('data-fs-threshold'), 10);
      if (entry.cents === serverThreshold && !bar.hasAttribute('hidden')) {
        return;
      }

      renderBar(bar, total, entry);
    });
  }

  /**
   * Watches the cart drawer, not the whole document: Dawn swaps the drawer's
   * contents wholesale on every cart mutation, and a body-wide subtree
   * observer fires on unrelated changes across the page.
   */
  function observeCartRerenders(callback) {
    var target =
      document.querySelector('cart-drawer') ||
      document.getElementById('CartDrawer') ||
      document.body;

    var scheduled = false;
    var observer = new MutationObserver(function () {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(function () {
        scheduled = false;
        callback();
      });
    });
    observer.observe(target, { childList: true, subtree: true });
  }

  /* ------------------------------------------------------------------ */

  function init() {
    if (!config || !config.thresholds) return;

    watchCountrySelection();

    if (core.prefs.hasCountry()) {
      console.log(
        LOG_PREFIX,
        'Manual country choice — keeping server-rendered threshold'
      );
      return;
    }

    var renderedEntry = config.renderedCountry
      ? config.thresholds[config.renderedCountry]
      : null;
    var renderedLabel = renderedEntry ? renderedEntry.label : null;

    core.geo.detect().then(function (countryCode) {
      if (!countryCode || countryCode === config.renderedCountry) return;

      var entry = config.thresholds[countryCode] || null;
      var label = entry ? entry.label : null;
      console.log(LOG_PREFIX, 'Detected country:', countryCode, '->', label);

      if (label !== renderedLabel) applyMarqueeLabel(label);
      adjustCartBars(countryCode);
      observeCartRerenders(function () {
        adjustCartBars(countryCode);
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
